/**
 * Search Executor
 * TASK-293: Single responsibility - Execute parallel searches
 *
 * Executes multiple search queries in parallel and aggregates results
 */

import { searchService } from '../../services/searchService';
import type { SearchRequest, SearchResponse, SourceType, SourceAttribution } from '../../types/search.types';
import type {
  SearchQueries,
  DateRangeInfo,
  MeetingPrepSource,
  MeetingPrepSourceType,
  AggregatedResults,
} from './types';

/**
 * Interface for search service dependency (for testability)
 */
export interface ISearchService {
  search(request: SearchRequest, signal?: AbortSignal): Promise<SearchResponse>;
}

/**
 * Default implementation using searchService
 */
const defaultSearchService: ISearchService = {
  search: (request: SearchRequest, signal?: AbortSignal) =>
    searchService.search(request, signal),
};

/**
 * Source configuration for each meeting prep source type
 */
const SOURCE_CONFIG: Record<MeetingPrepSourceType, SourceType[]> = {
  client: ['database'],
  email: ['email'],
  market: ['market'],
  news: ['news'],
  documents: ['document'],
  // TODO: Backend search tools needed — these map to closest existing types for now
  calendar: ['database'],
  portfolio: ['database'],
};

/**
 * Execute all searches in parallel
 *
 * @param queries - Search queries for each source
 * @param dateRange - Date range for filtering
 * @param signal - AbortSignal for cancellation
 * @param service - Optional search service for dependency injection
 * @param onSourceUpdate - Callback for per-source status updates
 * @returns Array of MeetingPrepSource with results
 */
export async function executeSearches(
  queries: SearchQueries,
  dateRange: DateRangeInfo,
  signal?: AbortSignal,
  service: ISearchService = defaultSearchService,
  onSourceUpdate?: (source: MeetingPrepSource) => void,
  clientId?: string,
): Promise<MeetingPrepSource[]> {
  const sourceTypes: MeetingPrepSourceType[] = ['client', 'email', 'market', 'news', 'documents', 'calendar', 'portfolio'];

  // Create initial pending states
  const results: MeetingPrepSource[] = sourceTypes.map((type) => ({
    type,
    status: 'pending' as const,
    data: null,
    itemCount: 0,
  }));

  // Execute searches in parallel
  const searchPromises = sourceTypes.map(async (sourceType, index) => {
    // Update to loading
    results[index] = { ...results[index], status: 'loading' };
    onSourceUpdate?.(results[index]);

    try {
      const request: SearchRequest = {
        query: queries[sourceType],
        sources: SOURCE_CONFIG[sourceType],
        dateFrom: sourceType !== 'client' && sourceType !== 'market' ? dateRange.dateFrom : undefined,
        dateTo: sourceType !== 'client' && sourceType !== 'market' ? dateRange.dateTo : undefined,
        includeSources: true,
        limit: 20,
        // TASK-400: Pass client context for fast-path routing
        clientId,
        entityType: clientId ? 'client' : undefined,
        entityId: clientId,
      };

      const response = await service.search(request, signal);

      // Update to completed or partial
      results[index] = {
        type: sourceType,
        status: response.status === 'partial' ? 'partial' : 'completed',
        data: response,
        itemCount: response.sources.length,
        error: response.warnings.length > 0 ? response.warnings[0] : undefined,
      };
    } catch (error: any) {
      // Handle cancellation
      if (error.name === 'AbortError') {
        results[index] = {
          type: sourceType,
          status: 'pending',
          data: null,
          itemCount: 0,
        };
      } else {
        results[index] = {
          type: sourceType,
          status: 'error',
          data: null,
          itemCount: 0,
          error: error.message || 'Search failed',
        };
      }
    }

    onSourceUpdate?.(results[index]);
    return results[index];
  });

  await Promise.allSettled(searchPromises);
  return results;
}

/**
 * Execute search for a single source (for refresh)
 */
export async function executeSourceSearch(
  sourceType: MeetingPrepSourceType,
  query: string,
  dateRange: DateRangeInfo,
  signal?: AbortSignal,
  service: ISearchService = defaultSearchService,
  clientId?: string,
): Promise<MeetingPrepSource> {
  try {
    const request: SearchRequest = {
      query,
      sources: SOURCE_CONFIG[sourceType],
      dateFrom: sourceType !== 'client' && sourceType !== 'market' ? dateRange.dateFrom : undefined,
      dateTo: sourceType !== 'client' && sourceType !== 'market' ? dateRange.dateTo : undefined,
      includeSources: true,
      limit: 20,
      // TASK-400: Pass client context for fast-path routing
      clientId,
      entityType: clientId ? 'client' : undefined,
      entityId: clientId,
    };

    const response = await service.search(request, signal);

    return {
      type: sourceType,
      status: response.status === 'partial' ? 'partial' : 'completed',
      data: response,
      itemCount: response.sources.length,
      error: response.warnings.length > 0 ? response.warnings[0] : undefined,
    };
  } catch (error: any) {
    if (error.name === 'AbortError') {
      return {
        type: sourceType,
        status: 'pending',
        data: null,
        itemCount: 0,
      };
    }

    return {
      type: sourceType,
      status: 'error',
      data: null,
      itemCount: 0,
      error: error.message || 'Search failed',
    };
  }
}

/**
 * Aggregate results from all sources
 */
export function aggregateResults(sources: MeetingPrepSource[]): AggregatedResults {
  const allSources = sources
    .filter((s) => s.data)
    .flatMap((s) => s.data!.sources);

  const bySource: Record<MeetingPrepSourceType, SourceAttribution[]> = {
    client: [],
    email: [],
    market: [],
    news: [],
    documents: [],
    calendar: [],
    portfolio: [],
  };

  sources.forEach((source) => {
    if (source.data) {
      bySource[source.type] = source.data.sources;
    }
  });

  const failedSources = sources
    .filter((s) => s.status === 'error')
    .map((s) => s.type);

  const warnings = sources
    .filter((s) => s.error)
    .map((s) => `${s.type}: ${s.error}`);

  // Combine answers from all sources
  const answers = sources
    .filter((s) => s.data?.answer)
    .map((s) => s.data!.answer);

  return {
    allSources,
    bySource,
    answer: answers.join('\n\n'),
    failedSources,
    warnings,
  };
}

/**
 * Check if all searches are complete
 */
export function isSearchComplete(sources: MeetingPrepSource[]): boolean {
  return sources.every(
    (s) => s.status === 'completed' || s.status === 'error' || s.status === 'partial'
  );
}

/**
 * Check if any search has an error
 */
export function hasSearchError(sources: MeetingPrepSource[]): boolean {
  return sources.some((s) => s.status === 'error');
}
